// HandOffModal — "Not my card": hand a receipt to the person it belongs to.
//
// The backstop for everything auto-routing cannot resolve: a shared card, a
// last4 the reader could not make out, a scan that failed outright. The blue
// callout higher up the review page is the OTHER door, the one-click "Send to
// <name>" the digest offers when it already knows whose card it was. This one
// asks.
//
// Picking a person IS the action, so there is no confirm button here: the
// picker's selection hands straight back to the page, which asks once more
// before the receipt leaves the queue.
//
// Shell, scrim, portal, focus trap and focus restore all come from the kit Dialog.
// The picker carries `data-drawer-autofocus` so the caret lands in the name
// field rather than on the close control.

import React from 'react';
import { Dialog } from '../components/ui/Dialog';
import { Alert } from '../components/ui/Alert';
import UserPicker from '../components/UserPicker';
import { buttonClass } from '../components/ui/Button';
import Reveal from '../components/ui/Reveal';

interface Props {
  /** The page keeps this mounted so the dialog can run its exit. */
  open?: boolean;
  users: any[];
  excludeIds: (string | number)[];
  /** The name list could not be fetched. An empty picker needs to say why. */
  loadFailed?: boolean;
  /** The name list has not arrived yet. Distinct from an empty list, which is an answer. */
  loading?: boolean;
  onSelect: (user: any) => void;
  onClose: () => void;
}

// Named here rather than inline so it stays a plain record on the way into a
// JS component: `data-*` and `aria-*` keys are not part of UserPicker's own
// inferred prop shape.
const PICKER_INPUT: Record<string, unknown> = {
  'data-drawer-autofocus': '',
  placeholder: 'Search by name',
  'aria-label': 'Search people by name',
};

const HandOffModal: React.FC<Props> = ({ open = true, users, excludeIds, loadFailed, loading, onSelect, onClose }) => (
  <Dialog open={open} title="Send this receipt to someone" onClose={onClose} size="md"
    footer={<button type="button" onClick={onClose} className={buttonClass('quiet', 'md')}>Cancel</button>}>
    <div>
      <p className="pb-4 text-sm text-on-surface-variant">
        They'll get it in their review queue. You won't file this one.
      </p>

      <UserPicker
        users={users}
        excludeIds={excludeIds}
        onSelect={onSelect}
        inputProps={PICKER_INPUT}
      />

      <Reveal show={!!loadFailed}>
        <div className="pt-4"><Alert variant="note">Could not load the list of people. Close this and try again.</Alert></div>
      </Reveal>
      <Reveal show={!!loading && !loadFailed}>
        <p className="pt-4 text-xs text-on-surface-variant">Loading names…</p>
      </Reveal>

    </div>
  </Dialog>
);

export default HandOffModal;
